import type { SiteRow } from '../../@types/types';
import type { SolarDesignRevision } from './preContractSolarDesign';
import {
    PRECONTRACT_DEFAULT_ESCALATOR,
    resolvePreContractEscalator,
} from './preContractSandboxPricing';

export type PreContractProduct = 'Solar' | 'Roof' | 'Battery' | 'RR' | 'RRR';

export type PreContractDesignTool = 'aurora' | 'hover';

/** Max-fill orientations Run AI will simulate and send to Gemini. */
export type PreContractFillOrientation = 'portrait' | 'landscape' | 'both';

export const DEFAULT_FILL_ORIENTATIONS: PreContractFillOrientation[] = [
    'portrait',
    'landscape',
];

export const FILL_ORIENTATION_OPTIONS: Array<{
    id: PreContractFillOrientation;
    label: string;
    hint: string;
}> = [
    { id: 'portrait', label: 'Portrait', hint: 'Fill all roofs in portrait' },
    { id: 'landscape', label: 'Landscape', hint: 'Fill all roofs in landscape' },
];

export function parseFillOrientations(value: unknown): PreContractFillOrientation[] {
    const allowed = new Set<PreContractFillOrientation>(DEFAULT_FILL_ORIENTATIONS);
    if (!Array.isArray(value)) return [];
    return value.filter(
        (item): item is PreContractFillOrientation =>
            item !== 'both' && allowed.has(item as PreContractFillOrientation),
    );
}

/** For max-fill / AI runs: empty selection means portrait and landscape are both allowed. */
export function normalizeFillOrientations(
    value: unknown,
): PreContractFillOrientation[] {
    const selected = parseFillOrientations(value);
    return selected.length > 0 ? selected : [...DEFAULT_FILL_ORIENTATIONS];
}

export function fillOrientationLabel(orientation: PreContractFillOrientation): string {
    if (orientation === 'landscape') return 'Landscape';
    if (orientation === 'both') return 'Both';
    return 'Portrait';
}

export function normalizeSiteTrees(value: unknown): PreContractSiteTree[] {
    if (!Array.isArray(value)) return [];
    return value
        .map((item) => {
            if (!item || typeof item !== 'object') return null;
            const row = item as Partial<PreContractSiteTree>;
            const id = String(row.id ?? '').trim();
            if (!id) return null;
            return {
                id,
                label: String(row.label ?? '').trim() || id,
                ghosted: row.ghosted === true,
                multiplier:
                    typeof row.multiplier === 'number' && Number.isFinite(row.multiplier)
                        ? row.multiplier
                        : 0,
                heightMeters:
                    typeof row.heightMeters === 'number' && Number.isFinite(row.heightMeters)
                        ? row.heightMeters
                        : undefined,
                trunkRadiusMeters:
                    typeof row.trunkRadiusMeters === 'number' && Number.isFinite(row.trunkRadiusMeters)
                        ? row.trunkRadiusMeters
                        : undefined,
            };
        })
        .filter((row): row is PreContractSiteTree => row != null);
}

export type PreContractModuleCell = {
    id: string;
    solarAccess: number;
    included: boolean;
    /** Stable oneDRAW mesh id so include toggles follow the physical module. */
    meshId?: string;
    /** oneDRAW module production multiplier after shade. */
    multiplier?: number;
    /** Estimated annual kWh for this module (power × multiplier). */
    productionKwh?: number;
    /** CSS fill from oneDRAW module.irrColor (matches 3D shade map). */
    shadeColor?: string;
} | null;

export type PreContractRoofModuleLayout = {
    roofIndex: number;
    /** Live oneDRAW roof tag when layouts come from the embed. */
    roofTagId?: number;
    label: string;
    cols: number;
    modules: PreContractModuleCell[];
    moduleOrientation?: 'portrait' | 'landscape';
};

/** One site tree from the 3D viewport, shown on the Tree Cutting tab. */
export type PreContractSiteTree = {
    id: string;
    label: string;
    ghosted: boolean;
    /** Production impact multiplier from oneDRAW shade. */
    multiplier: number;
    /** Tree height in meters from the viewer mesh (Tree Calculator). */
    heightMeters?: number;
    /** Trunk radius in meters from the viewer mesh (Tree Calculator DBH). */
    trunkRadiusMeters?: number;
};

export type PreContractIntakeForm = {
    products: Record<PreContractProduct, boolean>;
    installStreet: string;
    installCity: string;
    installState: string;
    installZip: string;
    utilityCompany: string;
    /** `Utility_company_id` from GetCompanies — passed to PPWDetails as `utilityId`. */
    utilityCompanyId: string;
    /** Utility / bill annual usage (kWh) — read-only on module step. */
    annualUsageKwh: string;
    /** Estimated system annual production (kWh) — editable; tracks panel selection. */
    systemUsageKwh: string;
    utilityRate: string;
    /** Target / minimum PPA rate ($/kWh) used for partner quoting. */
    targetMinPpaRate: string;
    /** Escalator fraction (e.g. `0.0299`) used for partner quoting. */
    targetEscalator: string;
    roofSelections: boolean[];
    /** Allow 30–50% SA (grey) modules in layout and AI picks. */
    includeGreyPanels: boolean;
    /** Allow <30% SA (purple) modules in layout and AI picks. */
    includePurplePanels: boolean;
    offsetPreference: 'full' | 'balanced' | null;
    /** Max-fill orientations to simulate for Run AI (multi-select). */
    fillOrientations: PreContractFillOrientation[];
    auroraAvailable: boolean;
    hoverAvailable: boolean;
    preferredDesignTool: PreContractDesignTool | null;
    moduleTypeName: string;
    moduleTypeWattage: number | null;
    treeCount: number;
    /** Live trees from the 3D viewport; ghosted trees are treated as cut. */
    siteTrees: PreContractSiteTree[];
    roofModuleLayouts: PreContractRoofModuleLayout[];
    /** oneDRAW Low/Normal roof squares (read-only totals from site). */
    roofSlopeLowTotal: number;
    roofSlopeNormalTotal: number;
    roofSlopeLowOverride: number | null;
    roofSlopeNormalOverride: number | null;
    batteryManufacturer: string;
    batteryName: string;
    batteryQuantity: number;
    /** How the current solar layout differs from the saved baseline. */
    solarDesignRevision: SolarDesignRevision | null;
    /** Short label for the applied AI layout (when solarDesignRevision is ai). */
    appliedAiDesignTitle: string | null;
};

export type PreContractSiteContext = {
    salesForceId: string;
    customerName: string;
    opportunityName: string;
    address: string;
    installStreet: string;
    installCity: string;
    installState: string;
    installZip: string;
    stage: string;
    salesPerson: string;
    accountId: string;
    dataSource: string;
};

const STORAGE_PREFIX = 'preContractIntake:';
const SITE_PREFIX = 'preContractSite:';

const STATE_NAME_TO_ABBREV: Record<string, string> = {
    Connecticut: 'CT',
    Massachusetts: 'MA',
    'New York': 'NY',
    'New Jersey': 'NJ',
    'Rhode Island': 'RI',
    Maine: 'ME',
    'New Hampshire': 'NH',
    Vermont: 'VT',
    Pennsylvania: 'PA',
};

function normalizeStateCode(state: string): string {
    const trimmed = state.trim();
    if (!trimmed) return '';
    if (trimmed.length === 2) return trimmed.toUpperCase();
    return STATE_NAME_TO_ABBREV[trimmed] ?? trimmed;
}

export function siteRowToContext(row: SiteRow): PreContractSiteContext {
    const customerName = row.opportunityName || 'Customer';
    const stateRaw = (row.state || '').trim();
    const zip = (row.zipCode || '').trim();
    const state = normalizeStateCode(stateRaw);
    let street = (row.address || '').trim();
    let city = '';

    const stripSuffix = (suffix: string) => {
        if (!suffix) return;
        const upperStreet = street.toUpperCase();
        const upperSuffix = suffix.toUpperCase();
        if (upperStreet.endsWith(` ${upperSuffix}`)) {
            street = street.slice(0, -(suffix.length + 1)).trim();
        } else if (upperStreet.endsWith(upperSuffix)) {
            street = street.slice(0, -suffix.length).trim();
        }
    };

    stripSuffix(stateRaw);
    stripSuffix(state);

    const tokens = street.split(/\s+/);
    if (tokens.length > 2) {
        city = tokens[tokens.length - 1] ?? '';
        street = tokens.slice(0, -1).join(' ');
    }

    return {
        salesForceId: row.salesForceId,
        customerName,
        opportunityName: row.opportunityName,
        address: row.address,
        installStreet: street,
        installCity: city,
        installState: state,
        installZip: zip,
        stage: row.stage,
        salesPerson: row.salesPerson,
        accountId: row.account_Id,
        dataSource: row.data_Source,
    };
}

export function defaultIntakeForm(ctx: PreContractSiteContext): PreContractIntakeForm {
    return {
        products: {
            Solar: false,
            Roof: false,
            Battery: false,
            RR: false,
            RRR: false,
        },
        installStreet: ctx.installStreet,
        installCity: ctx.installCity,
        installState: ctx.installState,
        installZip: ctx.installZip,
        utilityCompany: '',
        utilityCompanyId: '',
        annualUsageKwh: '',
        systemUsageKwh: '',
        utilityRate: '',
        targetMinPpaRate: '',
        targetEscalator: String(PRECONTRACT_DEFAULT_ESCALATOR),
        roofSelections: [true, true, true, true],
        includeGreyPanels: true,
        includePurplePanels: true,
        offsetPreference: null,
        fillOrientations: [],
        auroraAvailable: true,
        hoverAvailable: true,
        preferredDesignTool: 'aurora',
        moduleTypeName: '',
        moduleTypeWattage: null,
        treeCount: 0,
        siteTrees: [],
        roofModuleLayouts: [],
        roofSlopeLowTotal: 0,
        roofSlopeNormalTotal: 0,
        roofSlopeLowOverride: null,
        roofSlopeNormalOverride: null,
        batteryManufacturer: '',
        batteryName: '',
        batteryQuantity: 0,
        solarDesignRevision: null,
        appliedAiDesignTitle: null,
    };
}

function asRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : null;
}

function pickRecordStr(obj: Record<string, unknown> | null | undefined, keys: string[]): string {
    if (!obj) return '';
    for (const key of keys) {
        const value = obj[key];
        if (value == null) continue;
        const text = String(value).trim();
        if (text !== '') return text;
    }
    return '';
}

/** Build intake site context from OpportunityDetail so `/pre-contract?sfid=` can open without the sites table. */
export function siteContextFromOpportunity(
    salesForceId: string,
    opportunity: unknown,
): PreContractSiteContext {
    const row = asRecord(opportunity) ?? {};
    const addr = asRecord(row.InstallationAddress) ?? asRecord(row.installationAddress);
    const highlight = asRecord(row.OpportunityHighlight);
    const details = asRecord(row.OpportunityDetails);
    const salesPerson = asRecord(row.SalesPerson);
    const customers = Array.isArray(row.Customers)
        ? row.Customers
        : Array.isArray(row.customers)
          ? row.customers
          : [];
    const primary =
        customers.find((item) => {
            const contact = asRecord(item);
            return Boolean(contact?.IsPrimaryContact || contact?.isPrimaryContact);
        }) ?? customers[0];
    const contact = asRecord(primary);
    const customerName =
        [pickRecordStr(contact, ['FirstName', 'firstName']), pickRecordStr(contact, ['LastName', 'lastName'])]
            .filter(Boolean)
            .join(' ') ||
        pickRecordStr(row, ['Name', 'Opportunity_Name', 'OpportunityName']) ||
        'Customer';

    const street = pickRecordStr(addr, ['Street', 'street']);
    const city = pickRecordStr(addr, ['City', 'city']);
    const state = normalizeStateCode(pickRecordStr(addr, ['stateCode', 'State', 'state']));
    const zip = pickRecordStr(addr, ['ZipCode', 'zipCode']);
    const cityState = [city, state].filter(Boolean).join(', ');
    const address = [street, cityState, zip].filter(Boolean).join(' ');

    return {
        salesForceId,
        customerName,
        opportunityName:
            pickRecordStr(row, ['Opportunity_Name', 'OpportunityName', 'Name']) || customerName,
        address,
        installStreet: street,
        installCity: city,
        installState: state,
        installZip: zip,
        stage:
            pickRecordStr(row, ['Stage', 'stage', 'Stage_Status', 'stageStatus']) ||
            pickRecordStr(highlight, ['Stage', 'stage']) ||
            pickRecordStr(details, ['Stage', 'stage']) ||
            '',
        salesPerson: pickRecordStr(salesPerson, ['Name', 'name']),
        accountId: pickRecordStr(row, ['Account_Id', 'AccountId', 'account_Id']),
        dataSource: pickRecordStr(row, ['Data_Source', 'data_Source', 'DataSource']),
    };
}

export function persistSiteContext(ctx: PreContractSiteContext): void {
    try {
        sessionStorage.setItem(`${SITE_PREFIX}${ctx.salesForceId}`, JSON.stringify(ctx));
        sessionStorage.setItem('SalesForceId', ctx.salesForceId);
    } catch {
        /* ignore */
    }
}

export function loadSiteContext(sfid: string): PreContractSiteContext | null {
    try {
        const raw = sessionStorage.getItem(`${SITE_PREFIX}${sfid}`);
        if (!raw) return null;
        return JSON.parse(raw) as PreContractSiteContext;
    } catch {
        return null;
    }
}

export function persistIntakeForm(sfid: string, form: PreContractIntakeForm): void {
    try {
        sessionStorage.setItem(`${STORAGE_PREFIX}${sfid}`, JSON.stringify(form));
    } catch {
        /* ignore */
    }
}

export function loadIntakeForm(sfid: string): PreContractIntakeForm | null {
    try {
        const raw = sessionStorage.getItem(`${STORAGE_PREFIX}${sfid}`);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<PreContractIntakeForm> & {
            grayPanelsRequired?: 'yes' | 'no' | null;
        };
        const legacyAllow =
            parsed.grayPanelsRequired == null ? true : parsed.grayPanelsRequired === 'yes';
        const includeGreyPanels =
            typeof parsed.includeGreyPanels === 'boolean'
                ? parsed.includeGreyPanels
                : legacyAllow;
        const includePurplePanels =
            typeof parsed.includePurplePanels === 'boolean'
                ? parsed.includePurplePanels
                : legacyAllow;
        return {
            ...parsed,
            includeGreyPanels,
            includePurplePanels,
            preferredDesignTool:
                parsed.preferredDesignTool === 'aurora' || parsed.preferredDesignTool === 'hover'
                    ? parsed.preferredDesignTool
                    : parsed.auroraAvailable === false
                      ? null
                      : 'aurora',
            auroraAvailable: parsed.auroraAvailable !== false,
            hoverAvailable: parsed.hoverAvailable !== false,
            moduleTypeName: parsed.moduleTypeName ?? '',
            moduleTypeWattage: parsed.moduleTypeWattage ?? null,
            treeCount:
                typeof parsed.treeCount === 'number'
                    ? parsed.treeCount
                    : normalizeSiteTrees(parsed.siteTrees).length,
            siteTrees: normalizeSiteTrees(parsed.siteTrees),
            fillOrientations: parseFillOrientations(parsed.fillOrientations),
            systemUsageKwh:
                typeof parsed.systemUsageKwh === 'string' ? parsed.systemUsageKwh : '',
            targetMinPpaRate:
                typeof parsed.targetMinPpaRate === 'string' ? parsed.targetMinPpaRate : '',
            targetEscalator: String(resolvePreContractEscalator(parsed.targetEscalator as string)),
            roofModuleLayouts: Array.isArray(parsed.roofModuleLayouts)
                ? parsed.roofModuleLayouts
                : [],
            roofSlopeLowTotal:
                typeof parsed.roofSlopeLowTotal === 'number' ? parsed.roofSlopeLowTotal : 0,
            roofSlopeNormalTotal:
                typeof parsed.roofSlopeNormalTotal === 'number' ? parsed.roofSlopeNormalTotal : 0,
            roofSlopeLowOverride:
                typeof parsed.roofSlopeLowOverride === 'number' ? parsed.roofSlopeLowOverride : null,
            roofSlopeNormalOverride:
                typeof parsed.roofSlopeNormalOverride === 'number'
                    ? parsed.roofSlopeNormalOverride
                    : null,
            batteryManufacturer: parsed.batteryManufacturer?.trim() || '',
            batteryName: parsed.batteryName?.trim() || '',
            batteryQuantity:
                typeof parsed.batteryQuantity === 'number' && parsed.batteryQuantity > 0
                    ? parsed.batteryQuantity
                    : 0,
            solarDesignRevision:
                parsed.solarDesignRevision === 'ai' || parsed.solarDesignRevision === 'manual'
                    ? parsed.solarDesignRevision
                    : null,
            appliedAiDesignTitle:
                typeof parsed.appliedAiDesignTitle === 'string'
                    ? parsed.appliedAiDesignTitle.trim() || null
                    : null,
            ...(parsed.products
                ? {
                      products: clearDisabledPreContractProducts(
                          parsed.products as PreContractIntakeForm['products'],
                      ),
                  }
                : {}),
        } as PreContractIntakeForm;
    } catch {
        return null;
    }
}

export const PRE_CONTRACT_PRODUCTS: PreContractProduct[] = [
    'Solar',
    'Roof',
    'Battery',
    'RR',
    'RRR',
];

/** Temporarily unavailable in Pre-Contract product selection. */
export const PRE_CONTRACT_DISABLED_PRODUCTS: ReadonlySet<PreContractProduct> = new Set([
    'RR',
    'RRR',
]);

export function clearDisabledPreContractProducts(
    products: PreContractIntakeForm['products'],
): PreContractIntakeForm['products'] {
    if (!products) return products;
    const next = { ...products };
    for (const product of PRE_CONTRACT_DISABLED_PRODUCTS) {
        next[product] = false;
    }
    return next;
}

export const US_STATE_OPTIONS: { value: string; label: string }[] = [
    { value: 'CT', label: 'Connecticut' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'NY', label: 'New York' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'ME', label: 'Maine' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'VT', label: 'Vermont' },
    { value: 'PA', label: 'Pennsylvania' },
];

export const UTILITY_OPTIONS = [
    'United Illuminating',
    'Eversource (CL&P)',
    'National Grid',
    'Eversource',
    'Con Edison',
];
